"use client";

import { useQuery } from "@tanstack/react-query";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import AgencyTokenManager from "@/app/_module/services/agencyTokenManager";

/**
 * Hook for agency feature flags resolved with inheritance up the parent
 * agency chain. Used to gate features like Techficient Quote & Enroll
 * (`quoteAndEnroll`) — MRFG enables it on the top agency and every
 * sub-agency / agent inherits.
 *
 * The backend endpoint resolves the cascade; the hook just caches the result.
 */
export interface AgencyFeatures {
  /** Map of feature key → enabled (true) for this agency or any ancestor. */
  features: Record<string, boolean>;
  agencyId: string | null;
}

export const useAgencyFeatures = () => {
  // Scope the cache by the CURRENT agency. Without this, a multi-agency user
  // who switches from a flagged agency (e.g. one with quoteAndEnroll) to a
  // regular one keeps the stale flags for `staleTime`, so the UI shows the
  // flagged experience while the backend (correctly) rejects it → a spurious
  // "Feature quoteAndEnroll is not enabled" error. Keying by agencyId makes
  // the switch refetch immediately.
  const currentAgencyId = AgencyTokenManager.getCurrentAgencyId() || "none";
  return useQuery<AgencyFeatures>({
    queryKey: ["agency-features", currentAgencyId],
    queryFn: async () => {
      const resp = await handleApiCall(async () =>
        await api.get<{ features?: Record<string, boolean>; agencyId?: string }>({
          url: getApiUrl(endpoint.features.myAgency),
          auth: true,
        }),
      );
      const features = (resp as any)?.features || (resp as any) || {};
      return {
        features: typeof features === "object" ? features : {},
        agencyId: (resp as any)?.agencyId || null,
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes — flags rarely change
    gcTime: 30 * 60 * 1000,
    retry: 1,
  });
};

/**
 * Convenience hook: returns `{ enabled, isLoading }` for a single feature key.
 *
 * @example
 *   const { enabled, isLoading } = useFeatureFlag('quoteAndEnroll');
 */
export const useFeatureFlag = (key: string) => {
  const { data, isLoading, error } = useAgencyFeatures();
  return {
    enabled: !!data?.features?.[key],
    isLoading,
    error,
  };
};
